// TUT-490d5a — o KPI técnico da perícia: a seção inteira, gravar, confirmar
// (grava e confirma) e corrigir depois do protocolo do laudo, com motivo.
import { demoApi } from "../demo-data";
import type {
  AgenteDoKpi,
  CampoDeModeloDoKpi,
  ConsultaDeTextoDoKpi,
  CorrecaoDoKpi,
  CriarTextoDoKpi,
  GravacaoDoKpi,
  KpiDaPericia,
  KpiKind,
  PainelDoKpi,
  SalvarTextoDoKpi,
  SugestaoDeTextoDoKpi,
  TextoDoKpi,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

const doTipo = (expertiseId: string, kind: KpiKind) =>
  `/expertises/${encodeURIComponent(expertiseId)}/kpis/${encodeURIComponent(kind)}`;

export const apiDoKpiTecnico = {
  painelDoKpi(expertiseId: string): Promise<PainelDoKpi> {
    return useDemoApi
      ? demoApi.painelDoKpi(expertiseId)
      : request<PainelDoKpi>(`/expertises/${encodeURIComponent(expertiseId)}/kpis`);
  },
  gravarKpi(expertiseId: string, kind: KpiKind, input: GravacaoDoKpi): Promise<KpiDaPericia> {
    return useDemoApi
      ? demoApi.gravarKpi(expertiseId, kind, input)
      : request<KpiDaPericia>(doTipo(expertiseId, kind), { method: "PUT", body: JSON.stringify(input) });
  },
  confirmarKpi(expertiseId: string, kind: KpiKind, input: GravacaoDoKpi): Promise<KpiDaPericia> {
    return useDemoApi
      ? demoApi.confirmarKpi(expertiseId, kind, input)
      : request<KpiDaPericia>(`${doTipo(expertiseId, kind)}/confirm`, { method: "POST", body: JSON.stringify(input) });
  },
  corrigirKpi(expertiseId: string, kind: KpiKind, input: CorrecaoDoKpi): Promise<KpiDaPericia> {
    return useDemoApi
      ? demoApi.corrigirKpi(expertiseId, kind, input)
      : request<KpiDaPericia>(`${doTipo(expertiseId, kind)}/correction`, { method: "POST", body: JSON.stringify(input) });
  },

  // --- TUT-490d5b: os textos-padrão ---
  textosDoKpi(opcoes: { includeInactive?: boolean } = {}): Promise<TextoDoKpi[]> {
    if (useDemoApi) return demoApi.textosDoKpi(opcoes);
    const consulta = opcoes.includeInactive ? "?includeInactive=true" : "";
    return request<{ items: TextoDoKpi[] }>(`/kpi/textos${consulta}`).then((resposta) => resposta.items);
  },
  camposDosTextosDoKpi(): Promise<CampoDeModeloDoKpi[]> {
    if (useDemoApi) return demoApi.camposDosTextosDoKpi();
    return request<{ items: CampoDeModeloDoKpi[] }>("/kpi/textos/campos").then((resposta) => resposta.items);
  },
  agentesDosTextosDoKpi(): Promise<AgenteDoKpi[]> {
    if (useDemoApi) return demoApi.agentesDosTextosDoKpi();
    return request<{ items: AgenteDoKpi[] }>("/kpi/textos/agentes").then((resposta) => resposta.items);
  },
  criarTextoDoKpi(input: CriarTextoDoKpi): Promise<TextoDoKpi> {
    return useDemoApi
      ? demoApi.criarTextoDoKpi(input)
      : request<TextoDoKpi>("/kpi/textos", { method: "POST", body: JSON.stringify(input) });
  },
  novaVersaoDoTextoDoKpi(familyId: string, input: SalvarTextoDoKpi): Promise<TextoDoKpi> {
    return useDemoApi
      ? demoApi.novaVersaoDoTextoDoKpi(familyId, input)
      : request<TextoDoKpi>(`/kpi/textos/${encodeURIComponent(familyId)}/versions`, { method: "POST", body: JSON.stringify(input) });
  },
  definirTextoDoKpiAtivo(id: string, active: boolean): Promise<TextoDoKpi> {
    return useDemoApi
      ? demoApi.definirTextoDoKpiAtivo(id, active)
      : request<TextoDoKpi>(`/kpi/textos/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ active }) });
  },
  sugestoesDeTextoDoKpi(expertiseId: string, kind: KpiKind, consulta: ConsultaDeTextoDoKpi): Promise<SugestaoDeTextoDoKpi[]> {
    if (useDemoApi) return demoApi.sugestoesDeTextoDoKpi(expertiseId, kind, consulta);
    const params = new URLSearchParams({ field: consulta.field });
    if (consulta.agentId) params.set("agentId", consulta.agentId);
    if (consulta.ppeEffectiveness) params.set("ppeEffectiveness", consulta.ppeEffectiveness);
    if (consulta.lossProbability) params.set("lossProbability", consulta.lossProbability);
    if (consulta.local?.trim()) params.set("local", consulta.local.trim());
    return request<{ items: SugestaoDeTextoDoKpi[] }>(`${doTipo(expertiseId, kind)}/textos?${params.toString()}`).then(
      (resposta) => resposta.items,
    );
  },
};
